import type { IntakeSpec, FurnitureDeskDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { LEG_CROSS_SECTION, PANEL_THICKNESS } from '@/lib/intakeBuilderDefaults';

function getDetails(intake: IntakeSpec): FurnitureDeskDetails {
  return intake.details as FurnitureDeskDetails;
}

const DEFAULT_LENGTH = 1200;
const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 720;
const TOP_THICKNESS = 25;
const PEDESTAL_WIDTH = 400;
const PEDESTAL_THICKNESS = 18;

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const matKind = intake.core.primaryMaterial ?? 'hardwood';
  const topThickness =
    matKind === 'plywood' || matKind === 'mdf' ? PANEL_THICKNESS.plywood : TOP_THICKNESS;

  const deskL = intake.core.size?.length ?? DEFAULT_LENGTH;
  const deskW = intake.core.size?.width ?? DEFAULT_WIDTH;
  const deskH = intake.core.size?.height ?? DEFAULT_HEIGHT;
  const legSection = d.legStyle === 'panel' || d.legStyle === 'metal'
    ? (LEG_CROSS_SECTION[d.legStyle] || 30)
    : (LEG_CROSS_SECTION[d.legStyle] || 50);
  const legLength = deskH - topThickness;

  const out: Part[] = [];

  // Top panel
  out.push({
    id: 'top',
    name: '天板',
    kind: 'panel',
    materialId: 'mat-primary',
    width: deskL,
    depth: deskW,
    thickness: topThickness,
    position: [0, 0, deskH - topThickness],
    rotation: [0, 0, 0],
    tags: ['top'],
    paramRefs: [],
    cutouts: d.hasCableManagement
      ? [{ shape: 'rect', x: 0, y: 50, w: 80, h: 30, through: true }]
      : [],
  });

  if (d.deskType === 'simple' || d.deskType === 'standing') {
    // 4 legs
    const legInset = 60;
    const legPositions: Array<[number, number, number]> = [
      [-(deskL / 2 - legInset), -(deskW / 2 - legInset), 0],
      [deskL / 2 - legInset,   -(deskW / 2 - legInset), 0],
      [-(deskL / 2 - legInset), deskW / 2 - legInset,   0],
      [deskL / 2 - legInset,    deskW / 2 - legInset,   0],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `leg_${i + 1}`,
        name: `脚 ${i + 1}`,
        kind: 'leg',
        materialId: 'mat-primary',
        length: legLength,
        crossSection: { shape: 'square', side: legSection || 50 },
        position: legPositions[i],
        rotation: [0, 0, 0],
        tags: ['leg'],
        paramRefs: [],
      });
    }
  } else if (d.deskType === 'pedestal_single') {
    // 2 legs on one side + 1 pedestal on the other
    const legInset = 60;
    out.push(
      {
        id: 'leg_1',
        name: '脚 1',
        kind: 'leg',
        materialId: 'mat-primary',
        length: legLength,
        crossSection: { shape: 'square', side: legSection || 50 },
        position: [-(deskL / 2 - legInset), -(deskW / 2 - legInset), 0],
        rotation: [0, 0, 0],
        tags: ['leg'],
        paramRefs: [],
      },
      {
        id: 'leg_2',
        name: '脚 2',
        kind: 'leg',
        materialId: 'mat-primary',
        length: legLength,
        crossSection: { shape: 'square', side: legSection || 50 },
        position: [-(deskL / 2 - legInset), deskW / 2 - legInset, 0],
        rotation: [0, 0, 0],
        tags: ['leg'],
        paramRefs: [],
      },
      {
        id: 'pedestal',
        name: 'ペデスタル',
        kind: 'panel',
        materialId: 'mat-primary',
        width: PEDESTAL_WIDTH,
        depth: deskW,
        thickness: PEDESTAL_THICKNESS,
        position: [deskL / 2 - PEDESTAL_WIDTH / 2, 0, deskH / 2],
        rotation: [0, 0, 0],
        tags: ['pedestal'],
        paramRefs: [],
        cutouts: [],
      },
    );
  } else if (d.deskType === 'pedestal_double') {
    // 2 pedestal panels, no regular legs
    out.push(
      {
        id: 'pedestal_left',
        name: 'ペデスタル（左）',
        kind: 'panel',
        materialId: 'mat-primary',
        width: PEDESTAL_WIDTH,
        depth: deskW,
        thickness: PEDESTAL_THICKNESS,
        position: [-(deskL / 2 - PEDESTAL_WIDTH / 2), 0, deskH / 2],
        rotation: [0, 0, 0],
        tags: ['pedestal', 'left'],
        paramRefs: [],
        cutouts: [],
      },
      {
        id: 'pedestal_right',
        name: 'ペデスタル（右）',
        kind: 'panel',
        materialId: 'mat-primary',
        width: PEDESTAL_WIDTH,
        depth: deskW,
        thickness: PEDESTAL_THICKNESS,
        position: [deskL / 2 - PEDESTAL_WIDTH / 2, 0, deskH / 2],
        rotation: [0, 0, 0],
        tags: ['pedestal', 'right'],
        paramRefs: [],
        cutouts: [],
      },
    );
  } else if (d.deskType === 'l_shape') {
    // 4 legs for main top + return corner stub
    const legInset = 60;
    const legPositions: Array<[number, number, number]> = [
      [-(deskL / 2 - legInset), -(deskW / 2 - legInset), 0],
      [deskL / 2 - legInset,   -(deskW / 2 - legInset), 0],
      [-(deskL / 2 - legInset), deskW / 2 - legInset,   0],
      [deskL / 2 - legInset,    deskW / 2 - legInset,   0],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `leg_${i + 1}`,
        name: `脚 ${i + 1}`,
        kind: 'leg',
        materialId: 'mat-primary',
        length: legLength,
        crossSection: { shape: 'square', side: legSection || 50 },
        position: legPositions[i],
        rotation: [0, 0, 0],
        tags: ['leg'],
        paramRefs: [],
      });
    }
    // Return top stub
    out.push({
      id: 'return_top',
      name: 'リターン天板',
      kind: 'custom',
      materialId: 'mat-primary',
      openscadSnippet: '// L-shape return corner, V2',
      boundingBox: { w: 800, d: deskW, h: topThickness },
      position: [deskL / 2 + 400, 0, deskH - topThickness],
      rotation: [0, 0, 0],
      tags: ['top', 'return'],
      paramRefs: [],
    });
  }

  // Drawer fronts
  const drawerCount = d.drawerCount;
  if (drawerCount > 0) {
    const pedestalId = d.deskType === 'pedestal_double' ? 'pedestal_right' :
      d.deskType === 'pedestal_single' ? 'pedestal' : null;
    const drawerH = (deskH - topThickness) / (drawerCount + 1);
    for (let i = 0; i < drawerCount; i++) {
      out.push({
        id: `drawer_front_${i + 1}`,
        name: `引出し前板 ${i + 1}`,
        kind: 'panel',
        materialId: 'mat-primary',
        width: PEDESTAL_WIDTH - 4,
        depth: 15,
        thickness: drawerH - 10,
        position: [pedestalId ? deskL / 2 - PEDESTAL_WIDTH / 2 : 0, -(deskW / 2), drawerH * (i + 1)],
        rotation: [0, 0, 0],
        tags: ['drawer_front'],
        paramRefs: [],
        cutouts: [],
      });
    }
  }

  // Cable management back panel stub
  if (d.hasCableManagement) {
    out.push({
      id: 'back_panel',
      name: '背板（ケーブル管理）',
      kind: 'panel',
      materialId: 'mat-primary',
      width: deskL,
      depth: 15,
      thickness: deskH - topThickness,
      position: [0, deskW / 2, (deskH - topThickness) / 2],
      rotation: [0, 0, 0],
      tags: ['back', 'cable_management'],
      paramRefs: [],
      cutouts: [{ shape: 'rect', x: 0, y: 50, w: 80, h: 30, through: true }],
    });
  }

  return out;
}

export function joints(intake: IntakeSpec): Joint[] {
  const d = getDetails(intake);
  let joinery: string = d.joineryType;

  // cam_lock: no dedicated joint kind in B2 core / B2 rest. Treat as 'screw' for
  // V1 seed; agent can refine.
  if (joinery === 'cam_lock') joinery = 'screw';

  const matKind = intake.core.primaryMaterial ?? 'hardwood';
  const topThickness =
    matKind === 'plywood' || matKind === 'mdf' ? PANEL_THICKNESS.plywood : TOP_THICKNESS;
  const deskL = intake.core.size?.length ?? DEFAULT_LENGTH;
  const deskW = intake.core.size?.width ?? DEFAULT_WIDTH;
  const deskH = intake.core.size?.height ?? DEFAULT_HEIGHT;
  const legSection = d.legStyle === 'panel' || d.legStyle === 'metal'
    ? (LEG_CROSS_SECTION[d.legStyle] || 30)
    : (LEG_CROSS_SECTION[d.legStyle] || 50);
  const legLength = deskH - topThickness;
  const legInset = 60;

  // mortise_tenon: emit real MortiseTenonJoint between top and corner legs.
  // Only applies when the desk has corner legs (simple/standing/l_shape).
  if (
    joinery === 'mortise_tenon' &&
    (d.deskType === 'simple' || d.deskType === 'standing' || d.deskType === 'l_shape')
  ) {
    const out: Joint[] = [];
    const tenonLength = 25;
    const meetZ = legLength - 30;
    const halfLeg = legSection / 2;
    const corners: Array<[string, [number, number, number]]> = [
      ['leg_1', [-(deskL / 2 - legInset), -(deskW / 2 - legInset), -topThickness / 2]],
      ['leg_2', [deskL / 2 - legInset, -(deskW / 2 - legInset), -topThickness / 2]],
      ['leg_3', [-(deskL / 2 - legInset), deskW / 2 - legInset, -topThickness / 2]],
      ['leg_4', [deskL / 2 - legInset, deskW / 2 - legInset, -topThickness / 2]],
    ];
    let idx = 1;
    for (const [legId, topLocal] of corners) {
      out.push({
        id: `mt_${idx++}`,
        fromPartId: 'top',
        toPartId: legId,
        type: 'mortise_tenon',
        tenonWidth: 30,
        tenonHeight: 20,
        tenonLength,
        shoulder: 5,
        haunched: false,
        clearance: 0.2,
        axis: 'z',
        fromPosition: topLocal,
        toPosition: [0, 0, meetZ + halfLeg],
      });
    }
    return out;
  }

  // mortise_tenon on pedestal layouts has no rail to host it; fall back to dowel.
  if (joinery === 'mortise_tenon') joinery = 'dowel';
  // pocket_screw → treat as screw
  const effectiveJoinery = joinery === 'pocket_screw' ? 'screw' : joinery;

  const out: Joint[] = [];

  const hasLegs = d.deskType === 'simple' || d.deskType === 'standing' || d.deskType === 'l_shape';
  const hasMixedLegs = d.deskType === 'pedestal_single';

  if (hasLegs) {
    for (let i = 0; i < 4; i++) {
      if (effectiveJoinery === 'dowel') {
        out.push({ id: `top_to_leg_${i + 1}`, fromPartId: 'top', toPartId: `leg_${i + 1}`, type: 'dowel', diameter: 8, length: 40, count: 2 });
      } else {
        out.push({ id: `top_to_leg_${i + 1}`, fromPartId: 'top', toPartId: `leg_${i + 1}`, type: 'screw', size: 'M8', length: 60, count: 2 });
      }
    }
  } else if (hasMixedLegs) {
    for (let i = 1; i <= 2; i++) {
      if (effectiveJoinery === 'dowel') {
        out.push({ id: `top_to_leg_${i}`, fromPartId: 'top', toPartId: `leg_${i}`, type: 'dowel', diameter: 8, length: 40, count: 2 });
      } else {
        out.push({ id: `top_to_leg_${i}`, fromPartId: 'top', toPartId: `leg_${i}`, type: 'screw', size: 'M8', length: 60, count: 2 });
      }
    }
    if (effectiveJoinery === 'dowel') {
      out.push({ id: 'top_to_pedestal', fromPartId: 'top', toPartId: 'pedestal', type: 'dowel', diameter: 8, length: 40, count: 4 });
    } else {
      out.push({ id: 'top_to_pedestal', fromPartId: 'top', toPartId: 'pedestal', type: 'screw', size: 'M8', length: 60, count: 4 });
    }
  } else if (d.deskType === 'pedestal_double') {
    for (const pId of ['pedestal_left', 'pedestal_right']) {
      if (effectiveJoinery === 'dowel') {
        out.push({ id: `top_to_${pId}`, fromPartId: 'top', toPartId: pId, type: 'dowel', diameter: 8, length: 40, count: 4 });
      } else {
        out.push({ id: `top_to_${pId}`, fromPartId: 'top', toPartId: pId, type: 'screw', size: 'M8', length: 60, count: 4 });
      }
    }
  }

  return out;
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const out: Feature[] = [];

  // Edge chamfer 1mm on top edges
  out.push({
    id: 'chamfer_top',
    partId: 'top',
    kind: 'edge_treatment',
    treatment: 'chamfer',
    amount: 1,
    scope: 'top',
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    paramRefs: [],
  });

  // Cable management slot on back panel
  if (d.hasCableManagement) {
    out.push({
      id: 'cable_slot',
      partId: 'back_panel',
      kind: 'slot',
      length: 80,
      width: 30,
      depth: 18,
      axis: 'y',
      through: true,
      roundedEnds: true,
      position: [0, 0, 50],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  return out;
}
